import { useDisclosure } from '@mantine/hooks';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import {
  ContextProp,
  GroupedVirtuoso,
  GroupedVirtuosoHandle,
  VirtuosoHandle,
} from 'react-virtuoso';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { ListingTileItem } from 'src/tiles/ListingTilesV2';
import { PriceRangeGroupItem } from 'src/tiles/ListingTilesV2/CompListingTile';
import { ListingTile } from 'src/tiles/ListingTilesV2/ListingTile';
import { InventoryListingTileWrapper } from 'src/tiles/ListingTilesV2/ListingTilesV2.components';
import { useGroupedVirtuoso } from 'src/tiles/ListingTilesV2/ListingTilesV2.hooks';
import { isPriceRangeGroup } from 'src/tiles/ListingTilesV2/ListingTilesV2.utils';
import {
  Event,
  IListingGroupItem,
  Listing,
  ListingGroup,
  SectionInfo,
} from 'src/WebApiController';

import { MobileListingGroupStats } from '../components/MobileListingGroupStats';
import { useMobileListingDrawerContext } from '../MobileListingDrawerContext';
import {
  ListingGroupFloatingActions,
  ListingGroupVenueMap,
  MobileListingSubGroupTile,
} from './MobileListingGroupDetailsView.components';
import {
  useListingGroupCompListings,
  useListingGroupVirtuoso,
} from './MobileListingGroupDetailsView.hooks';

type ListingGroupDetailsVirtuosoContext = {
  listingGroup?: ListingGroup;
  subListings: Listing[];
  selectedSections: SectionInfo[];
  onSelectedSectionsChange: (selectedSections: SectionInfo[]) => void;
  showMap?: boolean;
  onBulkActionsClick: () => void;
};

const Header = ({
  context: {
    subListings,
    selectedSections,
    listingGroup,
    showMap,
    onSelectedSectionsChange,
    onBulkActionsClick,
  },
}: ContextProp<ListingGroupDetailsVirtuosoContext>) => {
  if (!listingGroup) {
    return null;
  }

  return (
    <Stack height="full" width="full" direction="column" gap="l">
      {showMap && (
        <ListingGroupVenueMap
          eventListings={subListings}
          compListings={[]}
          onUpdateSelectedSections={onSelectedSectionsChange}
          selectedSections={selectedSections}
        />
      )}
      <Stack
        direction="column"
        gap="l"
        style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.m}` }}
      >
        <MobileListingGroupStats
          listingGroup={listingGroup}
          onOpenActions={onBulkActionsClick}
        />
      </Stack>
    </Stack>
  );
};

export const MobileListingGroupDetailsView = ({
  event,
  onBulkActionsClick,
}: {
  event: Event;
  onBulkActionsClick: () => void;
}) => {
  const [showAllInventory, { toggle: toggleAllInventory }] = useDisclosure();
  const [showMap, { toggle: toggleMap }] = useDisclosure(true);

  const {
    selectedListingGroup,
    listingGroupPricingSettings,
    onSaveListingGroupPricingSettings,
  } = useMobileListingDrawerContext();
  const subListings = useMemo(
    () =>
      flattenListingGroup(selectedListingGroup).map(
        (l: IListingGroupItem): Listing => l as Listing
      ),
    [selectedListingGroup]
  );

  const { selectedSections, onSelectedSectionsChange, allInventoryWithGroups } =
    useListingGroupCompListings(
      event,
      subListings,
      listingGroupPricingSettings,
      onSaveListingGroupPricingSettings,
      showAllInventory
    );

  const virtuosoContext = useMemo<ListingGroupDetailsVirtuosoContext>(
    () => ({
      listingGroup: selectedListingGroup,
      subListings,
      selectedSections,
      showMap,
      onSelectedSectionsChange,
      onBulkActionsClick,
    }),
    [
      onSelectedSectionsChange,
      selectedListingGroup,
      selectedSections,
      showMap,
      subListings,
      onBulkActionsClick,
    ]
  );

  if (selectedListingGroup && isEmpty(subListings)) {
    return <PosSpinner size={vars.iconSize.s} />;
  }

  return (
    <>
      {!showAllInventory ? (
        <MyListingGroupView
          event={event}
          listingGroup={selectedListingGroup}
          context={virtuosoContext}
        />
      ) : (
        <AllInventoryListingGroupTiles
          event={event}
          listingGroup={selectedListingGroup}
          allInventoryWithGroups={allInventoryWithGroups}
          context={virtuosoContext}
        />
      )}
      <ListingGroupFloatingActions
        showAllInventory={showAllInventory}
        toggleAllInventory={toggleAllInventory}
        showMap={showMap}
        toggleMap={toggleMap}
      />
    </>
  );
};

const MyListingGroupView = ({
  event,
  listingGroup,
  context,
}: {
  event: Event;
  listingGroup?: ListingGroup;
  context: ListingGroupDetailsVirtuosoContext;
}) => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  useEffect(() => {
    // Scrolll to venue map by default
    if (context.showMap) {
      setTimeout(
        () =>
          virtuosoRef.current?.scrollToIndex({
            index: 0,
            align: 'end', // We want to scroll to the map, which is in the header, this is work around
            behavior: 'smooth',
          }),
        300
      );
    }
  }, [context.showMap]);

  const subGroups = useMemo(
    () => listingGroup?.groupItems ?? [],
    [listingGroup?.groupItems]
  );

  const { localGroupCounts, onUpdateExpanded, getGroupItemIndexFromItemIndex } =
    useListingGroupVirtuoso(subGroups);

  if (!listingGroup || !event) {
    return;
  }

  return (
    <GroupedVirtuoso<ListingGroup, ListingGroupDetailsVirtuosoContext>
      ref={virtuosoRef}
      context={context}
      components={{ Header: Header }}
      groupCounts={localGroupCounts}
      groupContent={(groupIndex: number) => {
        const groupItem = subGroups[groupIndex];
        if (!groupItem) {
          return null;
        }
        return (
          <div style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.xxs}` }}>
            <MobileListingSubGroupTile
              listingGroup={groupItem as ListingGroup}
              expanded={localGroupCounts[groupIndex] > 0}
              onUpdateExpanded={onUpdateExpanded(groupIndex)}
            />
          </div>
        );
      }}
      itemContent={(itemIndex: number, groupIndex: number) => {
        const groupListingIndex = getGroupItemIndexFromItemIndex(
          itemIndex,
          groupIndex
        );
        const groupItem = subGroups[groupIndex];
        if (!groupItem) {
          return null;
        }
        const listing = (groupItem as ListingGroup).groupItems?.[
          groupListingIndex
        ];
        if (!listing) {
          return null;
        }
        return (
          <div style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.xxs}` }}>
            <ListingTile
              listing={listing as Listing}
              event={event}
              entities={[]}
            />
          </div>
        );
      }}
    />
  );
};

const AllInventoryListingGroupTiles = ({
  event,
  listingGroup,
  context,
  allInventoryWithGroups,
}: {
  event: Event;
  listingGroup?: ListingGroup;
  context: ListingGroupDetailsVirtuosoContext;
  allInventoryWithGroups: ListingTileItem[];
}) => {
  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);

  useEffect(() => {
    // Scrolll to venue map by default
    if (context.showMap) {
      setTimeout(
        () =>
          virtuosoRef.current?.scrollToIndex({
            groupIndex: 0,
            align: 'end', // We want to scroll to the map, which is in the header, this is work around
            behavior: 'smooth',
          }),
        300
      );
    }
  }, [context.showMap]);

  const { localGroupCounts, onUpdateExpanded, getGroupItemIndexFromItemIndex } =
    useGroupedVirtuoso(allInventoryWithGroups);

  return (
    <GroupedVirtuoso<ListingTileItem, ListingGroupDetailsVirtuosoContext>
      ref={virtuosoRef}
      components={{ Header: Header }}
      context={context}
      groupCounts={localGroupCounts}
      groupContent={(groupIndex) => {
        return (
          <div style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.xxs}` }}>
            <InventoryListingTileWrapper
              listing={allInventoryWithGroups[groupIndex]}
              event={event}
              entities={[]}
              expanded={localGroupCounts[groupIndex] > 0}
              onUpdateExpanded={onUpdateExpanded(groupIndex)}
            />
          </div>
        );
      }}
      itemContent={(itemIndex, groupIndex) => {
        const group = allInventoryWithGroups[groupIndex];
        if (!isPriceRangeGroup(group)) {
          return null;
        }
        const groupItemIndex = getGroupItemIndexFromItemIndex(
          itemIndex,
          groupIndex
        );
        const item = group.groupItems[groupItemIndex];
        if (item) {
          return (
            <div style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.xxs}` }}>
              <PriceRangeGroupItem item={item} />
            </div>
          );
        }
      }}
    />
  );
};
